/**
 ⚠️ Design System — Protected file
 Do not edit this file without prior approval from the Design System team.
 */

import { v4 } from 'uuid'

import { createStore } from 'libs/store/createStore'
import { SnackBarProps } from 'ui/designSystem/Snackbar/SnackBar'

type Snackbar = {
  label: string
  type: 'success' | 'error'
  animationDuration: number
}

type State = { snackbars: Record<string, Snackbar> }

const defaultState: State = { snackbars: {} }

export const SHORT_TEXT_ANIMATION_DURATION_IN_MS = 5000
export const LONG_TEXT_ANIMATION_DURATION_IN_MS = 10000
export const LONG_TEXT_CHARACTER_LENGTH = 120
export const EXIT_THRESHOLD_IN_MS = 100 // keep snackbar a bit longer than the progress bar to avoid the progress bar animation after unmount

const getAnimationDuration = (label: string) =>
  label.length < LONG_TEXT_CHARACTER_LENGTH
    ? SHORT_TEXT_ANIMATION_DURATION_IN_MS
    : LONG_TEXT_ANIMATION_DURATION_IN_MS

export const snackBarStore = createStore({
  name: 'snackbar-store',
  defaultState,
  actions: (set) => ({
    showSuccessSnackBar: (label: string) => {
      snackBarStore.actions.open(label, 'success')
    },
    showErrorSnackBar: (label: string) => {
      snackBarStore.actions.open(label, 'error')
    },
    open: (label: string, type: 'success' | 'error') => {
      const id = v4()
      const animationDuration = getAnimationDuration(label)

      set((state) => ({
        snackbars: {
          ...state.snackbars,
          [id]: { label, type, animationDuration },
        },
      }))
      const timeout = setTimeout(() => {
        snackBarStore.actions.close(id)
        clearTimeout(timeout)
      }, animationDuration + EXIT_THRESHOLD_IN_MS)
    },
    close: (id: string) => {
      set((state) => {
        if (!snackBarStore.selectors.selectById(id)) {
          return state
        }

        const newState = { ...state.snackbars }
        delete newState[id]
        return { snackbars: newState }
      })
    },
    reset: () => set(() => defaultState),
  }),
  selectors: {
    selectById: (id: string) => (state) => state.snackbars[id],
    selectSnackbarProps:
      () =>
      (state): SnackBarProps[] => {
        const snackbars = state.snackbars
        return Object.entries(snackbars).map(([id, value]) => ({
          onClose: () => snackBarActions.close(id),
          id,
          ...value,
        }))
      },
  },
})

export const snackBarActions = snackBarStore.actions
export const { showErrorSnackBar, showSuccessSnackBar } = snackBarActions
export const { useSnackbarProps } = snackBarStore.hooks
